import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';

import { useIsomorphicLayoutEffect } from '@revenuemarket/react';
import { noop } from '@revenuemarket/utils';

import overlayEventContext, { ListenerCallbackParams } from '../events/event';
import { OverlayData } from '../types';

export interface OverlayContext {
  overlays: OverlayData[];
  setOverlays: React.Dispatch<React.SetStateAction<OverlayData[]>>;
  enable: () => void;
  disable: () => void;
  closeAll: () => void;
}

export const OverlayContext = createContext<OverlayContext>({
  overlays: [],
  setOverlays: noop,
  enable: noop,
  disable: noop,
  closeAll: noop,
});

const OverlayProvider = ({ children }: PropsWithChildren) => {
  const ctx = useContext(OverlayContext);
  const [disabled, setDisabled] = useState(false);
  const [overlays, setOverlays] = useState<OverlayData[]>([]);

  const closeAll = useCallback(() => {
    setOverlays([]);
    overlays.forEach((overlay) => overlayEventContext.notifyClose(overlay.id));
  }, [overlays, setOverlays]);

  useEffect(() => {
    const listenerCallback = (params: ListenerCallbackParams) => {
      if (disabled) return;
      switch (params.type) {
        case 'open':
          setOverlays((overlays) => [...overlays, params.data]);
          break;
        case 'close':
          setOverlays((overlays) => overlays.filter((overlay) => overlay.id !== params.data.id));
      }
    };
    overlayEventContext.addEventListener(listenerCallback);
    return () => {
      overlayEventContext.removeEventListener(listenerCallback);
    };
  }, [disabled]);

  const disable = useCallback(() => {
    setDisabled(true);
  }, []);

  const enable = useCallback(() => {
    setDisabled(false);
  }, []);

  useIsomorphicLayoutEffect(() => {
    ctx?.disable();
    return () => {
      ctx?.enable();
    };
  }, [ctx]);

  return (
    <OverlayContext.Provider value={{ overlays, setOverlays, enable, disable, closeAll }}>
      {children}
    </OverlayContext.Provider>
  );
};

export default OverlayProvider;
